/**
 * LoginControls — SuiteCommerce (Standard) 2026.1 extension entry point
 * Supplied Energy, LLC
 *
 * Rearranges the login/register page from 2 columns into 3:
 *   1. Returning customer  — the native login form (untouched)
 *   2. First time signing in? — hides the native register form, shows a button to the
 *      verify-first Suitelet register.suppliedenergy.com (email only, no password; new AND
 *      existing-offline customers — the Suitelet grants access + wires existing records with
 *      their preferred pricing/order history). The auto-login handoff (see
 *      docs/auto-login-handoff-design.md) brings them back into www logged-in.
 *   3. Become a Supplied Pro — the trade/contractor program (4 benefits + link to the
 *      existing application page).
 *
 * This is NOT the guest-checkout path. Guests are NEVER routed through this page (no sales
 * friction). This page is only for shoppers who proactively choose Login/Register from the
 * header, or navigate to it directly.
 *
 * v2.3.1 (2026-11-03) — header Design Tools dropdown widened 260px → 300px so "Enertech Unit System
 *   Builder" fits on one line (KT approved from mock-up). Default tool list gains Enertech System
 *   Builder and Vault Builder (both signed-in only); the live list is still whatever each domain's
 *   SC Configuration holds.
 *
 * v2.3.0 (2026-11-02) — DESIGN TOOLS MENU + STEADY HEADER LINK COLOURS.
 *   • Signed in, the header becomes "Design Tools ▾ | Orders | Logout". Design Tools is a click-to-open
 *     dropdown of the customer-facing design calculators (e.g. HDPE Loop Pressure Drop), each opening in
 *     a new tab. The list is NOT hard-coded: it is read from SuiteCommerce Configuration (Commerce >
 *     Websites > Configuration > Extensions > "Supplied Energy: Design Tools", per domain), so staff add or
 *     retire a tool without a deploy. If the configuration is missing, TOOLS_DEFAULT below is used; an
 *     explicitly emptied list (or Enabled unticked) hides the dropdown. Only https:// links are rendered,
 *     and every label/description goes in as text (never as HTML).
 *   • The dropdown lives inside our own injected <ul>, so it inherits all three v2.2.1 loop brakes. Tool
 *     links are left to the browser (target=_blank) — the capture handler only stops propagation so the
 *     SPA router can't swallow the click. Closes on outside click, Escape, or choosing a tool.
 *   • Header Login / Register no longer change colour after use. Each link's resting colour is measured
 *     (getComputedStyle never reports :visited colours, by browser privacy rule, so the reading is always
 *     the unvisited colour) and pinned for :visited and for mouse-click :focus. Keyboard focus
 *     (:focus-visible) and :hover still highlight. Self-calibrating, so it holds in every header layout
 *     (top bar, sidebar, checkout) without hard-coding each theme colour. Our own links got the same
 *     treatment: :focus → :focus-visible, so Orders no longer stays highlighted after Back.
 *   • Signed-in-only tools. Each tool has a "Signed-in customers only" flag in configuration.
 *     Signed OUT, the header shows "Design Tools ▾ | Login | Register": the dropdown lists the public
 *     tools, then "Sign in for more tools", which goes to the site's own login touchpoint. Signed IN,
 *     the header dropdown and the My Account menu list every tool. The signed-out items are two <li>s
 *     put at the front of the theme's own ul.header-profile-menu-login (a flex row): a separate <ul>
 *     beside it stacked onto its own line in the mock-up. They have their own classes and their own
 *     injection cap — installAccountLinks() deletes any .se-account-menu while signed out.
 *     NOTE this is a menu, not access control: a member-only tool's URL still opens for anyone who
 *     has it. Anything that must be private needs its own login on the tool side.
 *   • My Account side menu (myaccount app only): a "Design Tools" group sits between Billing and
 *     Settings, one entry per configured tool, via the supported MyAccountMenu component (addGroup /
 *     addGroupEntry). Core indexes measured on www 2026-11-02: Overview 0, Purchases 2, Wishlist 2,
 *     Billing 3, Settings 5, Cases 6 — so MENU_INDEX = 4. Entries are registered with a placeholder
 *     route (design-tools/N); each rendered anchor is rewritten to the tool's https URL with
 *     target=_blank, and the click is shielded from the SPA router exactly like the header links.
 *     Links inside any side-menu section no longer turn bold on hover (section names still do).
 *   • Signed-in links are white in the navy top bar. Since 2.2.0 they inherited the theme's grey
 *     (rgb 77,82,86) there and were barely legible; they now mirror the theme's Login/Register rule.
 *
 * v2.1.1 (2026-07-31) — enforces that. The native guest block is hidden on this page: the
 * "Checkout as a Guest" lede + button, the email form behind it, and the "Create Account"
 * opt-in beneath it. Previously the page still offered guest checkout, which contradicted the
 * design note above.
 *
 * v2.2.3 (2026-08-06) — LOGOUT ACTUALLY LOGS OUT.
 * 2.2.0 deliberately clicked the native signout element instead of reimplementing logout, reasoning
 * that SuiteCommerce would then run its own session-clear + redirect and no logout URL would be
 * guessed. Sound reasoning, wrong premise: a.header-profile-welcome-link's dropdown item
 * a[data-touchpoint="logout"] has NO working click handler in the shopping app. Measured live on
 * test 2026-08-06 — clicking it produced no navigation and no session change, and it still did
 * nothing after un-hiding its container, so this was never about our display:none. Shoppers were
 * left pressing Logout with no effect and the header stuck on "Orders | Logout".
 *
 * Now we navigate to SC.ENVIRONMENT.siteSettings.touchpoints.logout (/scs/logOut.ssp) — SuiteCommerce's
 * OWN configured logout touchpoint, read from the environment at click time, so it is no more a
 * guess than the native handler's would be, and it moves with the account's configuration. Verified
 * live: the session clears, the browser lands on /?logoff=T, our menu tears itself down and the
 * header returns to Login | Register on its own.
 * The native-element click is kept as a fallback for the case where the touchpoint is absent.
 *
 * v2.2.2 (2026-08-06) — hides the native "Welcome <name> ▾" greeting when our menu is installed.
 * 2.2.0/2.2.1 hid only .header-profile-menu-myaccount-container. In this theme (header_profile.tpl)
 * the greeting is a SIBLING that precedes it — a.header-profile-welcome-link, carrying the caret and
 * the data-toggle="dropdown" — so hiding the container left the greeting visible with nothing to
 * open: a control that responds to clicks by doing nothing. Confirmed live on test once 2.2.1 stopped
 * the freeze and the signed-in header could finally be seen at all. KT's call: hide the greeting
 * entirely, so the signed-in header is just "Orders | Logout" mirroring the logged-out
 * "Login | Register" pair — 2.2.0's stated intent.
 * The greeting is hidden, never removed, and the container still stays in the DOM: the native signout
 * element lives inside it and Logout works by clicking it. Both un-hide together when logged out or
 * when the re-inject cap gives up, so the fallback header is the complete native one.
 *
 * v2.2.1 (2026-08-06) — FIXES THE SIGNED-IN FREEZE INTRODUCED BY 2.2.0.
 * Symptom: any signed-in session on the shopping or myaccount app rendered partway and then the
 * renderer locked solid — no console error, no failed request. Guests were unaffected, and the
 * checkout app was unaffected (its header has no My Account dropdown), which is why the 2026-08-01
 * regression pass — run logged OUT on the login page, plus a checkout-app order — did not catch it.
 * Reproduced on test 2026-08-06 with the same bundle, so it is the code, not prod data.
 *
 * Cause: installAccountLinks() ran from a MutationObserver on document.body with NO debounce. It
 * inserts our <ul> as a sibling of the native My Account container. When the theme's header view
 * re-renders it drops that <ul>; the observer sees the removal, re-injects, the header re-renders
 * again — an unbounded inject/re-render ping-pong that pins the main thread. 2.2.0 anticipated a
 * flicker loop from REUSING theme classes and avoided that; this is the same loop reached by a
 * different route, so avoiding those classes was not enough on its own.
 *
 * Three independent brakes, any one of which stops the spin:
 *   1. DEBOUNCE — observer callbacks schedule a single run 120ms later (the pattern GuestCheckout
 *      already uses for the OPC's burst re-renders) instead of running synchronously per batch.
 *   2. SELF-MUTATION DEAFNESS — the observer is disconnected around our own DOM writes and any
 *      records they queued are discarded before reconnecting, so we never react to ourselves.
 *   3. RE-INJECT CAP — if the header removes our menu ACCT_MAX_INJECTS times, we stop fighting it,
 *      un-hide the native dropdown and leave the page alone. Fail visible: the shopper keeps a
 *      working header (native My Account + Sign Out) even in the worst case. A frozen page is the
 *      one outcome this file must never produce again.
 *
 * v2.2.0 (2026-07-31) — signed-in header links. When a shopper is logged in, the native header
 * shows a collapsible "My Account" dropdown (with Sign Out buried inside it). We replace that with
 * a flat "Orders | Logout" pair, visually mirroring the logged-out "Login | Register" pair.
 * Motivation: the silent-account / auto-login handoff flow lands shoppers logged-in and they need
 * an obvious way to reach their orders and to sign out. The injected links use OUR OWN classes
 * (styling matched via CSS), NOT the theme's header-profile-register-link class — reusing that
 * class would both fool isLoggedIn() (flip to "logged out" → add/remove flicker loop) and trip
 * installLinkInterceptor() (which redirects that class to the register Suitelet).
 *   • Logout does NOT reimplement anything — it programmatically clicks the native signout element
 *     (a.header-menu-myaccount-signout-link[data-touchpoint="logout"]), so SuiteCommerce runs its
 *     own session-clear + redirect (version-safe, no guessed logout URL).
 *   • Orders hard-navigates to /purchases (the myaccount order-history route the theme itself links
 *     to via href="/purchases"). Full navigation on purpose — bypasses the shopping-app SPA router,
 *     which has no /purchases route.
 *   • The native My Account dropdown is hidden (kept in the DOM so the signout click still works),
 *     never removed. Logged-in/out state is read from the header profile DOM (myaccount container
 *     vs login/register links). Clicks are handled in the capture phase so native delegated
 *     handlers on the reused link classes can't hijack them.
 *
 * ⚠️ SHOPPING / MYACCOUNT ONLY. This file also runs in the CHECKOUT app, where that same email
 * field is the shopper's only way into the wizard — SuiteCommerce gates the wizard on an
 * identified session, and the email is what opens it. Hiding it there would make checkout
 * impossible. `isCheckoutApp()` gates the guest-hide rules accordingly; GuestCheckout owns the
 * checkout route and reshapes it down to that single field. Do not merge the two rule sets.
 *
 * Supersedes the VerifyFirstRegistration extension (front-end routing) — do not run both. The
 * verify-first Suitelet 4038 stays deployed (it is the middle column's destination + wiring).
 *
 * Front-end only. DOM manipulation + injected CSS, because "rearrange columns + swap a form
 * for a button + add a column" isn't covered by the Layout/addChildView model.
 *
 * Live-confirmed structure (test.suppliedenergy.com, checkout touchpoint, 2026.1):
 *   Container:  .login-register-body  (currently display:block, 2 form columns @ ~50%)
 *   Login col:  .login-register-wrapper-column-login
 *   Reg col:    .login-register-wrapper-column-register  (register form + the old
 *               .tt-supplied-pro-banner nested underneath)
 *   Empty CMS:  .login-register-wrapper-column-cms-area-{above-login,between-login-register,below-register}
 *   Reg form:   .login-register-register-form
 */
define(
	'SuppliedEnergy.LoginControls.LoginControls'
,	[]
,	function () {
	'use strict';

	// --- CONFIG (labels/links kept here so they're easy to tweak) -------
	var SUITELET_URL     = 'https://register.suppliedenergy.com/';                  // middle column
	var SUPPLIED_PRO_URL = 'https://info.suppliedenergy.com/become-a-supplied-pro'; // 3rd column (real target; /become-a-supplied-pro 404s on test)
	var BLUE = '#005487';
	var STYLE_ID = 'pwless-3col-style';
	var ACCOUNT_STYLE_ID = 'se-account-links-style';
	var ACCOUNT_MENU_CLASS = 'se-account-menu';   // our injected <ul> (mirrors .header-profile-menu-login)
	var ACCT_LINK_CLASS = 'se-acct-link';          // marker on each injected <a>, keyed by data-se
	var ORDERS_URL = '/purchases';                 // myaccount order-history route (theme links here too)
	var ACCT_MAX_INJECTS = 3;                      // give up re-injecting after this many (see v2.2.1)
	var OBSERVER_DELAY = 120;                      // ms; headers re-render in bursts

	// --- DESIGN TOOLS (v2.3.0) ------------------------------------------
	// Source of truth is SC Configuration (Configuration/LoginControls.json defines the fields). This
	// default is only the fallback for a domain whose configuration doesn't carry the key at all.
	var TOOLS_CONFIG_KEY = 'suppliedEnergy.designTools';
	var TOOLS_DEFAULT = {
		enabled: true,
		label: 'Design Tools',
		items: [
			{
				label: 'HDPE Loop Pressure Drop',
				url: 'https://claude.ai/artifact/LytNh13x7XsxeasAgpZnV1?sk=MipFdtmv59yEUxyvnTmclg',
				description: 'Geothermal loop head, Geo-Flo pump selection & HDPE pipe sizing',
				signedInOnly: true            // KT 2026-11-02: only Homeowner Submittal is public
			},
			{
				label: 'Enertech System Builder',  // KT 2026-11-03; name sized to fit the 213px My Account menu
				url: 'https://claude.ai/artifact/9RUVtYoQ7C1fZozXSUrwN2?sk=mrJ1KpxwbLL7sD6Ps8cP_g',
				description: 'Build an Enertech geothermal system and send it to Support',
				signedInOnly: true
			},
			{
				label: 'Vault Builder',
				url: 'https://claude.ai/artifact/6VrQbmKCS6FsRg3QDZv9J5?sk=hYqgHVdh0NNL26lgpUhb7A',
				description: 'Size a geothermal vault from your loop field and get a quote',
				signedInOnly: true
			}
		]
	};
	var TOOLS_CLASS = 'se-tools';                  // the dropdown's <li> inside our account menu
	var TOOLS_OPEN_CLASS = 'se-open';
	var TOOL_LINK_CLASS = 'se-tool-link';
	var GUEST_TOOLS_CLASS = 'se-guest-tools';      // signed-out "Design Tools ▾" <li>, ahead of Login | Register
	var GUEST_SEP_CLASS = 'se-guest-sep';          // its "|" <li>
	var SIGNIN_TEXT = 'Sign in for more tools';
	var guestInjects = 0;                          // same brake as acctInjects, for the signed-out menu
	var guestGaveUp = false;
	var appContainer = null;                       // set in mountToApp (for the Environment component)

	// My Account side menu (v2.3.0). Billing is index 3 and Settings 5 (measured), so 4 lands between.
	var MENU_GROUP_ID = 'se_design_tools';
	var MENU_ENTRY_PREFIX = 'se_tool_';
	var MENU_INDEX = 4;
	var menuToolUrls = {};                         // data-id -> https URL, filled by registerAccountMenu()
	var MENU_STYLE_ID = 'se-account-menu-tools-style';
	// The theme bolds every My Account menu link on hover (font-weight 600). Bold "HDPE Loop Pressure
	// Drop" is wider than the 253px side menu, so it jumped to two lines under the pointer (seen live
	// 2026-11-02). KT's call: section names (Purchases, Billing, Design Tools…) keep bolding on hover;
	// the links INSIDE a section don't — they still change colour. Applies to every section, not only
	// ours, so the menu behaves consistently. The current page's link (.active) stays bold.
	var MENU_CSS = [
		'.menu-tree-node-submenu .menu-tree-node-item-anchor:not(.active):hover,',
		'.menu-tree-node-submenu .menu-tree-node-item-anchor:not(.active):focus',
		'{font-weight:inherit !important;}'
	].join('');

	// --- HEADER LINK COLOURS (v2.3.0) -----------------------------------
	var HEADER_LINK_STYLE_ID = 'se-header-link-style';
	var HEADER_LINK_SEL = 'a.header-profile-login-link, a.header-profile-register-link';

	// --- LOOP BRAKES (v2.2.1) -------------------------------------------
	var acctInjects = 0;      // how many times we have inserted our menu this page load
	var acctGaveUp  = false;  // latched once the header has won ACCT_MAX_INJECTS times
	var observer    = null;   // the body observer, so we can go deaf during our own writes
	var scheduled   = false;  // debounce latch

	var MIDDLE_HTML =
		'<div class="pwless-panel pwless-mid">' +
			'<h2>First time signing in?</h2>' +
			'<p>New here, or ordered with us before? Set up your online account here for order tracking and history.</p>' +
			'<a class="button-primary pwless-btn" href="' + SUITELET_URL + '">Set up online access &rarr;</a>' +
		'</div>';

	var PRO_HTML =
		'<div class="pwless-panel pwless-pro">' +
			'<h2>Become a Supplied Pro</h2>' +
			'<p>Our free program for approved contractors, installers, and trade professionals:</p>' +
			'<ul>' +
				'<li>Preferred Pricing</li>' +
				'<li>Product Training &amp; Education</li>' +
				'<li>Marketing Support Opportunities</li>' +
				'<li>Installer-Focused Support</li>' +
			'</ul>' +
			'<a class="button-primary pwless-btn" href="' + SUPPLIED_PRO_URL + '">Become a Supplied Pro &rarr;</a>' +
		'</div>';

	// This file runs on shopping, myaccount AND checkout. The guest-block rules below must NOT
	// apply in the checkout app: there, that same email field is the shopper's only way in, and
	// GuestCheckout reshapes the route around it. Detect the checkout app by a module only it
	// defines, falling back to the path. Wrong answer degrades to "not checkout", i.e. the safe
	// side for the standalone login page this extension exists to control.
	function isCheckoutApp() {
		try {
			if (typeof window.require === 'function' &&
				window.require('SC.Checkout.Configuration.Steps.Standard')) { return true; }
		} catch (e) { /* module absent → not the checkout app */ }
		try { return String(window.location.pathname || '').indexOf('checkout.ssp') !== -1; }
		catch (e) { return false; }
	}

	// --- No guest checkout on the login page (KT, 2026-07-31) ---------------
	// This store does not surface NetSuite's guest-checkout UI. A shopper only reaches this page
	// by choosing Login/Register deliberately, and a deliberate visitor is not looking to check
	// out as a guest. So the whole native guest block goes here: its lede, the "Checkout as a
	// Guest" button, the email form behind it, the <hr>, and the "Create Account" opt-in beneath.
	// SHOPPING / MYACCOUNT ONLY — see isCheckoutApp() above.
	var NO_GUEST_CSS = [
		'.login-register-wrapper-guest{display:none !important;}',
		// "Create an account and take advantage of faster checkouts and other great benefits."
		// renders TWICE — once inside the guest block above, once above the First-time panel.
		// Hiding the block only kills the first; this kills the survivor.
		'.login-register-register-form-description{display:none !important;}'
	].join('');

	var CSS = [
		'.login-register-body{display:flex !important;flex-wrap:wrap;gap:32px;align-items:flex-start;}',
		'.login-register-wrapper-column-cms-area-above-login,',
		'.login-register-wrapper-column-cms-area-between-login-register,',
		'.login-register-wrapper-column-cms-area-below-register{display:none !important;}',
		// The old "free shipping" Supplied Pro banner is CMS content that loads async (after
		// build3Col latches) — hide it via CSS so it's gone whenever it renders. We rebuild it
		// as the proper 3rd column (.pwless-col-pro) with the correct benefits.
		'.tt-supplied-pro-banner{display:none !important;}',
		'.login-register-wrapper-column-login,.login-register-wrapper-column-register,.pwless-col-pro{',
			'flex:1 1 260px !important;width:auto !important;min-width:0;box-sizing:border-box;}',
		'.pwless-panel h2{color:' + BLUE + ';font-size:20px;margin:0 0 10px;}',
		// Match the "Returning customer" heading color to the other two columns (theme default is dark).
		'.login-register-login-title{color:' + BLUE + ' !important;}',
		'.pwless-panel p{line-height:1.5;margin:0 0 14px;}',
		'.pwless-panel ul{margin:0 0 18px;padding-left:18px;line-height:1.9;}',
		// Buttons reuse the theme .button-primary class (yellow bg + dark text + the exact hover:
		// darker yellow, white text, lift) so all three match the native Log In button. .pwless-btn
		// only fixes anchor geometry (inline-block, no underline) to the Log In button's size.
		'.pwless-btn{display:inline-block !important;text-decoration:none;padding:18px 25px;font-size:1rem;border-radius:8px;line-height:1;}',
		'@media (max-width:767px){.login-register-body{flex-direction:column;}}'
	].join('');

	// Signed-in header links: "Orders | Logout", shown in place of the native My Account dropdown and
	// visually mirroring the logged-out "Login | Register" pair. Uses OUR OWN classes only (styling
	// in ACCOUNT_CSS) — reusing the theme's header-profile-register-link class would fool isLoggedIn()
	// (→ flicker loop) and trip installLinkInterceptor() (→ redirect to the register Suitelet).
	// Behavior is bound via the ACCT_LINK_CLASS marker + data-se (see the capture-phase interceptor).
	var ACCOUNT_MENU_HTML =
		'<ul class="' + ACCOUNT_MENU_CLASS + '">' +
			'<li><a class="' + ACCT_LINK_CLASS + '" data-se="orders" href="' + ORDERS_URL + '">Orders</a></li>' +
			'<li class="se-acct-sep" aria-hidden="true">|</li>' +
			'<li><a class="' + ACCT_LINK_CLASS + '" data-se="logout" href="#">Logout</a></li>' +
		'</ul>';

	// Our menu styling (plain header text-links with a " | " divider, matching the login/register
	// pair) + hiding the native My Account dropdown while our menu shows. The dropdown is only hidden,
	// never removed, so the native signout click still fires. Scoped to the flagged container so the
	// logged-out header is untouched. color:inherit makes the links pick up the header's text color.
	var ACCOUNT_CSS = [
		'.header-profile-menu-myaccount-container.se-hidden{display:none !important;}',
		// v2.2.2: the greeting anchor (name + caret + data-toggle) sits BESIDE the container, not
		// inside it. Without this it survives as a dead control once the dropdown is hidden.
		'.header-profile-welcome-link.se-hidden{display:none !important;}',
		'.' + ACCOUNT_MENU_CLASS + '{list-style:none;margin:0;padding:0;display:flex;align-items:center;gap:8px;}',
		'.' + ACCOUNT_MENU_CLASS + ' li{display:inline-flex;align-items:center;}',
		'.' + ACCOUNT_MENU_CLASS + ' .se-acct-sep{opacity:.5;}',
		'.' + GUEST_SEP_CLASS + '{opacity:.5;padding:0 8px;}',
		'.' + ACCT_LINK_CLASS + '{color:inherit;text-decoration:none;font-size:14px;line-height:1;white-space:nowrap;cursor:pointer;}',
		// v2.3.0: :focus-visible, not :focus — a mouse click (or Back restoring focus) must not leave a
		// link stuck in its highlight colour. Keyboard users still get it.
		'.' + ACCT_LINK_CLASS + ':hover,.' + ACCT_LINK_CLASS + ':focus-visible{color:' + BLUE + ';text-decoration:underline;}',
		// v2.3.0: in the navy top bar the inherited colour is the theme's grey (rgb 77,82,86 — measured on
		// www), which left "Orders | Logout" barely legible. Mirror the theme's own rule for Login/Register
		// in that same slot: white at rest, on hover and on focus (underline marks hover).
		'.tt-header-mte-style-secondary .tt-header-mte-group > [data-view="Header.Profile"] .' + ACCT_LINK_CLASS + ',',
		'.tt-header-mte-style-secondary .tt-header-mte-group > [data-view="Header.Profile"] .' + ACCT_LINK_CLASS + ':hover,',
		'.tt-header-mte-style-secondary .tt-header-mte-group > [data-view="Header.Profile"] .' + ACCT_LINK_CLASS + ':focus-visible,',
		'.tt-header-mte-style-secondary .tt-header-mte-group > [data-view="Header.Profile"] .' + ACCOUNT_MENU_CLASS + ' .se-acct-sep,',
		'.tt-header-mte-style-secondary .tt-header-mte-group > [data-view="Header.Profile"] .' + GUEST_SEP_CLASS + '{color:#fff;}',
		// Design Tools toggle is a <button>; strip button chrome so it reads as one more header link.
		'button.' + ACCT_LINK_CLASS + '{background:none;border:0;padding:0;margin:0;font:inherit;font-size:14px;}',
		'.' + TOOLS_CLASS + '{position:relative;}',
		'.se-tools-caret{display:inline-block;margin-left:4px;font-size:12px;transition:transform .15s;}',
		'.' + TOOLS_CLASS + '.' + TOOLS_OPEN_CLASS + ' .se-tools-caret{transform:rotate(180deg);}',
		'.se-tools-menu{display:none;position:absolute;top:calc(100% + 10px);right:0;z-index:100001;',
			'list-style:none;margin:0;padding:6px 0;min-width:300px;max-width:calc(100vw - 32px);',
			'background:#fff;border-radius:10px;box-shadow:0 8px 24px rgba(0,43,73,.22);text-align:left;}',
		'.' + TOOLS_CLASS + '.' + TOOLS_OPEN_CLASS + ' .se-tools-menu{display:block;}',
		'.se-tools-menu li{display:block !important;}',
		'a.' + TOOL_LINK_CLASS + ',a.' + TOOL_LINK_CLASS + ':visited{display:block;padding:8px 16px;color:#002b49 !important;text-decoration:none !important;white-space:normal;line-height:1.3;}',
		'a.' + TOOL_LINK_CLASS + ':hover,a.' + TOOL_LINK_CLASS + ':focus-visible{background:#eef4f9;color:' + BLUE + ' !important;}',
		'.se-tool-name{display:block;font-size:14px;font-weight:600;}',
		'.se-tool-name:after{content:"\\2197";margin-left:6px;font-size:11px;opacity:.55;}',
		'.se-tool-desc{display:block;margin-top:2px;font-size:12px;color:#5a6570;}',
		'.se-tools-signin-row{border-top:1px solid #e3e8ed;margin-top:4px;padding-top:4px;}',
		'a.se-tools-signin .se-tool-name{color:' + BLUE + ';}',
		'a.se-tools-signin .se-tool-name:after{content:"\\2192";}'
	].join('');

	// v2.3.0: header Login / Register keep their resting colour after being visited or clicked.
	// --se-base-color is measured per link by calibrateHeaderLinks(); the data-se-base guard means the
	// rule only ever applies once a real measurement exists (an unset var() would fall back to inherit).
	var HEADER_LINK_CSS = [
		'a.header-profile-login-link[data-se-base]:visited:not(:hover),',
		'a.header-profile-register-link[data-se-base]:visited:not(:hover),',
		'a.header-profile-login-link[data-se-base]:focus:not(:focus-visible):not(:hover),',
		'a.header-profile-register-link[data-se-base]:focus:not(:focus-visible):not(:hover)',
		'{color:var(--se-base-color) !important;}'
	].join('');

	// --- HELPERS --------------------------------------------------------
	function injectStyle() {
		if (document.getElementById(STYLE_ID)) { return; }
		var s = document.createElement('style');
		s.id = STYLE_ID;
		s.textContent = CSS + (isCheckoutApp() ? '' : NO_GUEST_CSS);
		(document.head || document.documentElement).appendChild(s);
	}

	function injectAccountStyle() {
		if (document.getElementById(ACCOUNT_STYLE_ID)) { return; }
		var s = document.createElement('style');
		s.id = ACCOUNT_STYLE_ID;
		s.textContent = ACCOUNT_CSS;
		(document.head || document.documentElement).appendChild(s);
	}

	function injectHeaderLinkStyle() {
		if (document.getElementById(HEADER_LINK_STYLE_ID)) { return; }
		var s = document.createElement('style');
		s.id = HEADER_LINK_STYLE_ID;
		s.textContent = HEADER_LINK_CSS;
		(document.head || document.documentElement).appendChild(s);
	}

	// v2.3.0: record each header Login/Register link's resting colour on the link itself. Skipped while
	// the link is hovered or focused (that would record the highlight). Attribute/style writes only —
	// the body observer watches childList, so this can never schedule another pass.
	function calibrateHeaderLinks() {
		var links = document.querySelectorAll(HEADER_LINK_SEL);
		if (!links.length) { return; }
		injectHeaderLinkStyle();
		for (var i = 0; i < links.length; i++) {
			var a = links[i];
			try {
				if (a.matches(':hover') || a.matches(':focus')) { continue; }
				var c = getComputedStyle(a).color;    // never the :visited colour (browser privacy rule)
				if (!c) { continue; }
				if (a.getAttribute('data-se-base') !== c) {
					a.style.setProperty('--se-base-color', c);
					a.setAttribute('data-se-base', c);
				}
			} catch (e) { /* old browser without :focus-visible etc. — leave the link alone */ }
		}
	}

	// --- DESIGN TOOLS (v2.3.0) ------------------------------------------
	// Read a dotted SC Configuration path: the extension Environment component first (the supported
	// API), then SC.CONFIGURATION directly. undefined = not configured on this domain.
	function readConfig(path) {
		try {
			var env = appContainer && appContainer.getComponent && appContainer.getComponent('Environment');
			if (env && typeof env.getConfig === 'function') {
				var v = env.getConfig(path);
				if (v !== undefined && v !== null) { return v; }
			}
		} catch (e) { /* fall through */ }
		try {
			var o = window.SC && SC.CONFIGURATION;
			var parts = path.split('.');
			for (var i = 0; i < parts.length; i++) {
				if (o === undefined || o === null) { return undefined; }
				o = o[parts[i]];
			}
			return o;
		} catch (e) { return undefined; }
	}

	function trimmed(v) { return (typeof v === 'string') ? v.replace(/^\s+|\s+$/g, '') : ''; }

	function toolsConfig() {
		var enabled = readConfig(TOOLS_CONFIG_KEY + '.enabled');
		var label = trimmed(readConfig(TOOLS_CONFIG_KEY + '.label'));
		var raw = readConfig(TOOLS_CONFIG_KEY + '.items');
		if (Object.prototype.toString.call(raw) !== '[object Array]') { raw = TOOLS_DEFAULT.items; }
		var items = [];
		for (var i = 0; i < raw.length; i++) {
			var it = raw[i] || {};
			var name = trimmed(it.label);
			var url = trimmed(it.url);
			if (!name || !/^https:\/\//i.test(url)) { continue; }   // https only — no javascript:, no relative
			var only = it.signedInOnly;
			items.push({
				label: name,
				url: url,
				description: trimmed(it.description),
				signedInOnly: only === true || only === 'T' || only === 'true'
			});
		}
		return {
			enabled: enabled !== false && enabled !== 'F' && enabled !== 'false',
			label: label || TOOLS_DEFAULT.label,
			items: items
		};
	}

	function el(tag, cls, text) {
		var n = document.createElement(tag);
		if (cls) { n.className = cls; }
		if (text) { n.appendChild(document.createTextNode(text)); }
		return n;
	}

	// <li class="se-tools"><button …>Design Tools ▾</button><ul class="se-tools-menu">…</ul></li>
	// Built with DOM calls + text nodes so nothing from configuration is ever parsed as HTML.
	function loginUrl() {
		var tp = null;
		try { tp = window.SC && SC.ENVIRONMENT && SC.ENVIRONMENT.siteSettings && SC.ENVIRONMENT.siteSettings.touchpoints && SC.ENVIRONMENT.siteSettings.touchpoints.login; }
		catch (e) { tp = null; }
		if (typeof tp !== 'string' || !tp) { tp = '/scs/checkout.ssp?is=login&login=T'; }
		return tp.indexOf('#') === -1 ? tp + '#login-register' : tp;
	}

	// The tools a shopper may see listed: everything when signed in, public ones when not.
	function visibleTools(cfg, signedIn) {
		var out = [];
		for (var i = 0; i < cfg.items.length; i++) {
			if (signedIn || !cfg.items[i].signedInOnly) { out.push(cfg.items[i]); }
		}
		return out;
	}

	function buildToolsItem(cfg, signedIn) {
		var items = visibleTools(cfg, signedIn !== false);
		var li = el('li', TOOLS_CLASS);
		var btn = el('button', ACCT_LINK_CLASS + ' se-tools-toggle', cfg.label);
		btn.type = 'button';
		btn.setAttribute('data-se', 'tools');
		btn.setAttribute('aria-haspopup', 'true');
		btn.setAttribute('aria-expanded', 'false');
		var caret = el('span', 'se-tools-caret', '\u25BE');
		caret.setAttribute('aria-hidden', 'true');
		btn.appendChild(caret);
		li.appendChild(btn);

		var list = el('ul', 'se-tools-menu');
		for (var i = 0; i < items.length; i++) {
			var t = items[i];
			var a = el('a', TOOL_LINK_CLASS);
			a.href = t.url;
			a.target = '_blank';
			a.rel = 'noopener';
			a.appendChild(el('span', 'se-tool-name', t.label));
			if (t.description) { a.appendChild(el('span', 'se-tool-desc', t.description)); }
			var item = el('li');
			item.appendChild(a);
			list.appendChild(item);
		}
		if (signedIn === false) {
			// Same tab, on purpose: this is the site's own login, not a tool.
			var si = el('a', TOOL_LINK_CLASS + ' se-tools-signin');
			si.href = loginUrl();
			si.setAttribute('data-se', 'signin');
			si.appendChild(el('span', 'se-tool-name', SIGNIN_TEXT));
			var sli = el('li', items.length ? 'se-tools-signin-row' : '');
			sli.appendChild(si);
			list.appendChild(sli);
		}
		li.appendChild(list);
		return li;
	}

	// v2.3.0: signed-out header. "Design Tools ▾ |" goes in front of the theme's Login | Register.
	// Mirrors installAccountLinks(): idempotent, removed when signed in, capped re-injection.
	function removeGuestTools() {
		var olds = document.querySelectorAll('.' + GUEST_TOOLS_CLASS + ', .' + GUEST_SEP_CLASS);
		for (var i = 0; i < olds.length; i++) { if (olds[i].parentNode) { olds[i].parentNode.removeChild(olds[i]); } }
	}

	function installGuestTools() {
		var row = document.querySelector('.header-profile-menu-login-container ul.header-profile-menu-login');
		var tools = toolsConfig();
		if (isLoggedIn() || !row || guestGaveUp || !tools.enabled) { removeGuestTools(); return; }
		if (row.querySelector('.' + GUEST_TOOLS_CLASS)) { return; }
		guestInjects = guestInjects + 1;
		if (guestInjects > ACCT_MAX_INJECTS) { guestGaveUp = true; removeGuestTools(); return; }   // header wins
		injectAccountStyle();
		var item = buildToolsItem(tools, false);
		item.className = TOOLS_CLASS + ' ' + GUEST_TOOLS_CLASS;
		var sep = el('li', 'se-acct-sep ' + GUEST_SEP_CLASS, '|');
		sep.setAttribute('aria-hidden', 'true');
		row.insertBefore(sep, row.firstChild);
		row.insertBefore(item, sep);
	}

	function setToolsOpen(li, open) {
		if (!li) { return; }
		if (open) { li.classList.add(TOOLS_OPEN_CLASS); } else { li.classList.remove(TOOLS_OPEN_CLASS); }
		var btn = li.querySelector('.se-tools-toggle');
		if (btn) { btn.setAttribute('aria-expanded', open ? 'true' : 'false'); }
	}

	function closeTools() {
		var open = document.querySelectorAll('.' + TOOLS_CLASS + '.' + TOOLS_OPEN_CLASS);
		for (var i = 0; i < open.length; i++) { setToolsOpen(open[i], false); }
	}

	// v2.3.0: My Account side menu group. Only the myaccount app exposes MyAccountMenu; elsewhere
	// getComponent returns nothing and this is a no-op. Never throws — a menu problem must not stop
	// the rest of this extension from mounting.
	function registerAccountMenu(container) {
		try {
			var menu = container && container.getComponent && container.getComponent('MyAccountMenu');
			if (!menu || typeof menu.addGroup !== 'function' || typeof menu.addGroupEntry !== 'function') { return; }
			var cfg = toolsConfig();
			if (!cfg.enabled || !cfg.items.length) { return; }
			menu.addGroup({ id: MENU_GROUP_ID, name: cfg.label, index: MENU_INDEX });
			for (var i = 0; i < cfg.items.length; i++) {
				var id = MENU_ENTRY_PREFIX + (i + 1);
				menuToolUrls[id] = cfg.items[i].url;
				menu.addGroupEntry({
					groupid: MENU_GROUP_ID,
					id: id,
					name: cfg.items[i].label,
					url: 'design-tools/' + (i + 1),        // placeholder route; the anchor is rewritten below
					index: i + 1
				});
			}
		} catch (e) { /* leave the menu as the platform built it */ }
	}

	// Point each rendered Design Tools anchor at its real URL in a new tab. Attribute writes only.
	function pointMenuAnchor(a) {
		var url = menuToolUrls[a.getAttribute('data-id')];
		if (!url) { return false; }
		if (a.getAttribute('href') !== url) { a.setAttribute('href', url); }
		if (a.getAttribute('target') !== '_blank') { a.setAttribute('target', '_blank'); }
		if (a.getAttribute('rel') !== 'noopener') { a.setAttribute('rel', 'noopener'); }
		return true;
	}

	function fixAccountMenuLinks() {
		var anchors = document.querySelectorAll('a[data-id^="' + MENU_ENTRY_PREFIX + '"]');
		if (document.querySelector('.menu-tree') && !document.getElementById(MENU_STYLE_ID)) {
			var st = document.createElement('style');
			st.id = MENU_STYLE_ID;
			st.textContent = MENU_CSS;
			(document.head || document.documentElement).appendChild(st);
		}
		for (var i = 0; i < anchors.length; i++) { pointMenuAnchor(anchors[i]); }
	}

	function firstChildFromHtml(html) {
		var wrap = document.createElement('div');
		wrap.innerHTML = html;
		return wrap.firstChild;
	}

	// --- CORE -----------------------------------------------------------
	// Rearrange into 3 columns. Idempotent (guarded by data-pwless3 on the body).
	function build3Col() {
		var body = document.querySelector('.login-register-body');
		if (!body) { return; }

		var loginCol = body.querySelector('.login-register-wrapper-column-login');
		var regCol   = body.querySelector('.login-register-wrapper-column-register');
		if (!loginCol || !regCol) { return; }

		var form = regCol.querySelector('.login-register-register-form');
		if (!form) { return; }                       // register column not rendered yet — observer retries

		injectStyle();
		if (body.getAttribute('data-pwless3') === '1') { return; }

		// Hide the old (wrong-verbiage: "free shipping") Supplied Pro banner nested in the
		// register column — we rebuild it as a proper 3rd column below.
		var oldBanner = body.querySelector('.tt-supplied-pro-banner');
		if (oldBanner) {
			var ob = (oldBanner.closest && oldBanner.closest('.cms-content')) || oldBanner;
			ob.style.display = 'none';
		}

		// Middle column: hide the native register form, drop in the "First time signing in?" panel.
		// Hide the native "New customer" heading (wrong wording for existing-offline customers).
		var regHeading = regCol.querySelector('h1, h2, h3, h4');
		if (regHeading && /new customer/i.test(regHeading.textContent || '')) { regHeading.style.display = 'none'; }
		form.parentNode.insertBefore(firstChildFromHtml(MIDDLE_HTML), form);
		form.style.display = 'none';

		// 3rd column: build the Supplied Pro column after the register column
		// (visible order becomes login | register | pro; the empty CMS columns are hidden by CSS).
		var proCol = document.createElement('div');
		proCol.className = 'pwless-col-pro';
		proCol.appendChild(firstChildFromHtml(PRO_HTML));
		// Match the theme card (background, rounded corners, padding, shadow) EXACTLY by copying
		// the register column's computed card styling — so the 3rd column looks identical and its
		// heading lines up vertically with the other two (they all get the same 30px top padding).
		try {
			var ref = getComputedStyle(regCol);
			['backgroundColor', 'borderRadius', 'padding', 'boxShadow'].forEach(function (p) { proCol.style[p] = ref[p]; });
		} catch (e) { /* older browsers */ }
		regCol.parentNode.insertBefore(proCol, regCol.nextSibling);

		body.setAttribute('data-pwless3', '1');
	}

	// --- SIGNED-IN HEADER LINKS ("Orders | Logout") ---------------------
	// NetSuite's native Sign Out lives inside the collapsible My Account dropdown. Find that native
	// element — we click it rather than reimplement logout (see file header, v2.2.0).
	function nativeSignoutLink() {
		return document.querySelector('.header-menu-myaccount-signout-link, [data-touchpoint="logout"]');
	}

	// v2.2.2: the theme's "Welcome <name> ▾" anchor. Sibling of the My Account container (see
	// header_profile.tpl), so it needs hiding separately or it outlives the dropdown it opens.
	function welcomeLink() {
		return document.querySelector('a.header-profile-welcome-link');
	}

	// Show or hide the whole native signed-in header (greeting + dropdown) as one unit. Hidden only,
	// never removed — Logout clicks the native signout element inside the container.
	function setNativeProfileHidden(hidden) {
		var parts = [document.querySelector('.header-profile-menu-myaccount-container'), welcomeLink()];
		for (var i = 0; i < parts.length; i++) {
			if (!parts[i]) { continue; }
			if (hidden) { parts[i].classList.add('se-hidden'); }
			else { parts[i].classList.remove('se-hidden'); }
		}
	}

	// Read logged-in state from the header profile DOM. The profile view renders EITHER the login/
	// register links (logged out) OR the My Account menu with the signout link (logged in) — never
	// both — so the login/register links are an authoritative "logged out" signal, and a present
	// signout link is the "logged in" signal. If the header hasn't rendered yet, neither matches
	// and we report false (safe: no swap until we know they're signed in).
	function isLoggedIn() {
		if (document.querySelector('.header-profile-login-link, .header-profile-register-link')) { return false; }
		return !!nativeSignoutLink();
	}

	// Swap the native My Account dropdown for our "Orders | Logout" pair (or undo the swap when
	// logged out). Idempotent; re-run freely from the observer as the SPA re-renders the header.
	function installAccountLinks() {
		var container = document.querySelector('.header-profile-menu-myaccount-container');
		var existing = document.querySelector('.' + ACCOUNT_MENU_CLASS);

		// v2.2.1 brake 3: the header keeps removing our menu, so stop fighting it. The native
		// dropdown goes back on and stays on — a usable header beats a frozen page.
		if (acctGaveUp) {
			setNativeProfileHidden(false);              // hand the shopper back the full native header
			return;
		}

		if (!isLoggedIn() || !container) {
			// Logged out (or header not rendered yet) — undo any prior swap so the native header is clean.
			if (existing && existing.parentNode) { existing.parentNode.removeChild(existing); }
			setNativeProfileHidden(false);
			return;
		}

		setNativeProfileHidden(true);                   // hide greeting + dropdown (kept in DOM for signout)
		if (existing) { return; }                       // our menu already injected

		// Our menu is gone but we are still logged in — the header re-rendered over it. Count the
		// rebuild; past the cap we concede rather than trade DOM writes with the theme forever.
		acctInjects = acctInjects + 1;
		if (acctInjects > ACCT_MAX_INJECTS) {
			acctGaveUp = true;
			setNativeProfileHidden(false);
			return;
		}

		injectAccountStyle();
		var menu = firstChildFromHtml(ACCOUNT_MENU_HTML);
		// v2.3.0: "Design Tools ▾ |" ahead of "Orders | Logout", when configured.
		var tools = toolsConfig();
		if (tools.enabled && tools.items.length) {
			var sep = firstChildFromHtml('<li class="se-acct-sep" aria-hidden="true">|</li>');
			menu.insertBefore(sep, menu.firstChild);
			menu.insertBefore(buildToolsItem(tools, true), menu.firstChild);
		}
		container.parentNode.insertBefore(menu, container.nextSibling);
	}

	// v2.2.3: sign out via SuiteCommerce's own configured logout touchpoint. Read from the environment
	// at click time (not cached at load) so it tracks the account's configuration. The native-element
	// click stays as a fallback, even though it was measured dead on test — if a future SCS version
	// wires it up and drops the touchpoint, this still works.
	function logoutTouchpoint() {
		try {
			var tp = window.SC && SC.ENVIRONMENT && SC.ENVIRONMENT.siteSettings &&
				SC.ENVIRONMENT.siteSettings.touchpoints;
			var url = tp && tp.logout;
			return (typeof url === 'string' && url) ? url : null;
		} catch (e) { return null; }
	}

	function doLogout() {
		var url = logoutTouchpoint();
		if (url) { window.location.href = url; return; }
		var signout = nativeSignoutLink();     // fallback: re-query at click time, SPA may have re-rendered
		if (signout) { signout.click(); }
	}

	// Capture-phase handler for our injected links. Capture beats the SPA router and any bubble-phase
	// Backbone handlers bound to the reused theme classes, so those can't hijack these clicks.
	function installAccountLinkInterceptor() {
		document.addEventListener('click', function (e) {
			var a = e.target && e.target.closest && e.target.closest('.' + ACCT_LINK_CLASS);
			if (!a) { return; }
			e.preventDefault();
			e.stopPropagation();
			if (a.getAttribute('data-se') === 'tools') {
				var li = a.closest('.' + TOOLS_CLASS);
				var wasOpen = !!(li && li.classList.contains(TOOLS_OPEN_CLASS));
				closeTools();
				setToolsOpen(li, !wasOpen);
				return;
			}
			if (a.getAttribute('data-se') === 'logout') {
				doLogout();
			} else {
				// Orders: hard navigation to the myaccount route (the shopping SPA has no /purchases).
				window.location.assign(ORDERS_URL);
			}
		}, true);                                       // capture phase
	}

	// v2.3.0: Design Tools dropdown housekeeping, all capture phase.
	//   • A tool link: stop propagation ONLY (no preventDefault) — the browser opens it in a new tab
	//     natively and the SPA router never sees an external URL. Then close the menu.
	//   • Any click outside an open dropdown closes it. Escape closes it and returns focus to the toggle.
	function installToolsInterceptor() {
		document.addEventListener('click', function (e) {
			var t = e.target;
			// My Account menu entry: make sure the href is the real tool (even if this click beat the
			// render pass), then keep the SPA router away. The browser opens it in a new tab natively.
			var m = t && t.closest && t.closest('a[data-id^="' + MENU_ENTRY_PREFIX + '"]');
			if (m && pointMenuAnchor(m)) {
				e.stopPropagation();
				return;
			}
			if (t && t.closest && t.closest('a.' + TOOL_LINK_CLASS)) {
				e.stopPropagation();
				closeTools();
				return;
			}
			if (t && t.closest && t.closest('.' + TOOLS_CLASS)) { return; }   // toggle handled above
			closeTools();
		}, true);
		document.addEventListener('keydown', function (e) {
			if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
			var open = document.querySelector('.' + TOOLS_CLASS + '.' + TOOLS_OPEN_CLASS);
			if (!open) { return; }
			closeTools();
			var btn = open.querySelector('.se-tools-toggle');
			if (btn) { btn.focus(); }
		}, true);
	}

	// Header "Register" link → the Suitelet (not the native register view).
	function installLinkInterceptor() {
		document.addEventListener('click', function (e) {
			var a = e.target && e.target.closest &&
				e.target.closest('a.header-profile-register-link, a.pwless-force-register');
			if (!a) { return; }
			e.preventDefault();
			e.stopPropagation();
			window.location.href = SUITELET_URL;
		}, true);                                    // capture phase: beats the SPA router
	}

	// --- ENTRY POINT ----------------------------------------------------
	return {
		mountToApp: function mountToApp (container) {
			if (typeof document === 'undefined' || typeof window === 'undefined') { return; }
			appContainer = container || null;
			registerAccountMenu(container);              // before the layout renders, so it shows on first paint

			installLinkInterceptor();
			installAccountLinkInterceptor();
			installToolsInterceptor();

			// v2.2.1 brake 2: go deaf during our own DOM writes and bin the records they queue, so a
			// pass can never be the reason the next pass runs. This — not idempotency — is what makes
			// the observer safe; 2.2.0 relied on the guards alone and spun anyway.
			function runPass() {
				if (observer) { observer.disconnect(); }
				try {
					build3Col();
					installAccountLinks();
					try { installGuestTools(); } catch (e) { /* signed-out extra only — never block a pass */ }
					try { calibrateHeaderLinks(); } catch (e) { /* cosmetic only — never block a pass */ }
					try { fixAccountMenuLinks(); } catch (e) { /* click handler re-points as a fallback */ }
				} finally {
					if (observer) {
						observer.takeRecords();                  // discard what we just caused
						observer.observe(document.body, { childList: true, subtree: true });
					}
				}
			}

			// v2.2.1 brake 1: coalesce mutation bursts into one pass instead of running per batch.
			function schedulePass() {
				if (scheduled) { return; }
				scheduled = true;
				setTimeout(function () { scheduled = false; runPass(); }, OBSERVER_DELAY);
			}

			// The header renders async and re-renders on SPA navigation, so re-run both when the DOM
			// settles. Observe document.body (not just #main): the header profile — where the My Account
			// dropdown and our "Orders | Logout" menu live — renders OUTSIDE #main, so a #main-scoped
			// observer would miss it.
			observer = new MutationObserver(schedulePass);
			runPass();                                   // first pass also arms the observer

			window.addEventListener('hashchange', function () {
				// A real navigation: the header legitimately rebuilds, so the shopper gets a fresh
				// budget of injections rather than inheriting the previous route's tally.
				acctInjects = 0;
				acctGaveUp = false;
				guestInjects = 0;
				guestGaveUp = false;
				schedulePass();
			});

			// v2.3.0: header layouts swap at breakpoints, and with them the links' resting colour.
			// Re-measure after a resize (debounced through the same single-pass latch).
			window.addEventListener('resize', schedulePass);
		}
	};
});
